// import { useState, useEffect } from "react";
import React, { CSSProperties, useMemo, useEffect, useState, useRef } from "react";
import {
  contract,
  ProgressBar,
  Modal,
  Frame,
  List,
  TitleBar,
  Button,
  Input,
} from "@react95/core";

import { Lock, Ie, Mshtml32529, Progman44, Progman45 } from "@react95/icons";

import { useApp } from "../../context/AppContext";
import WebsiteProvider, { useWebsite } from "../../context/WebsiteContext";

import { CustomButton } from "../UI/CustomButton";
import { IExploreFile, IExploreGoInactive, IExploreGoActive } from "../UI/IconImage";

import { WINDOW_NAMES } from "../../config/const";
import { getModalSizeProps } from "../../config/window";

import { HorizDivider, VertDivider, DoubleVertDivider } from "../UI/Dividers";

import HomePage from "./Websites/Home";
import DefaultPage from "./Websites/Default";
import CTFPage from "./Websites/CTF";

// website controller -  used for navigation
function WebsiteController() {
  const { currentURL } = useWebsite();

  const currentUrlBase = useMemo(() => {
    try {
      const url = new URL(currentURL);
      return url.origin;
    } catch {
      return currentURL;
    }
  }, [currentURL]);

  const pageMap: Record<string, React.FC> = {
    "http://internet.dom": DefaultPage,
    "http://home.dom": HomePage,
    "http://ctf.dom": CTFPage,

    // "http://dog.dom": CTFPage, // feed the dog spaghetti
    // "http://board.dom": CTFPage, // message board
    // "http://tasks.dom": CTFPage, // issue tracker
    // "http://dev.dom": CTFPage, // developer blog
    // "http://memes.dom": CTFPage, // daily meme, repository
    //
  };

  const PageComponent = useMemo(() => pageMap[currentUrlBase], [currentUrlBase, pageMap]);
  if (PageComponent) {
    return <PageComponent />;
  }

  return null;
}

function TopButtonToolbar() {
  const { canGoBack, canGoForward, goBack, goForward } = useWebsite();

  const buttonStyle: CSSProperties = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    gap: "2px",
    height: "24x",
  };

  return (
    <Frame
      style={{
        display: "flex",
        flexDirection: "row",
        gap: "4px",
        marginBottom: "4px",
        marginTop: "4px",
        margin: "2px",
      }}
    >
      <DoubleVertDivider />
      <CustomButton style={buttonStyle} disabled={!canGoBack} onClick={goBack}>
        <Progman44 variant="32x32_4" style={{ width: "20px", height: "20px" }} />
        Back
      </CustomButton>
      <CustomButton style={buttonStyle} disabled={!canGoForward} onClick={goForward}>
        <Progman45 variant="32x32_4" style={{ width: "20px", height: "20px" }} />
        Forward
      </CustomButton>
    </Frame>
  );
}

function AddressBar() {
  const { currentURL, pageTitle, pageIcon, navigateToURL } = useWebsite();

  const [urlValue, setUrlValue] = useState(currentURL);
  const [goButtonHover, setGoButtonHover] = useState(false);

  // listen for change in currentURL
  useEffect(() => {
    setUrlValue(currentURL);
  }, [currentURL]);

  // note: handle enter on the address input itself (use onKeyDown on the input) - avoid global listener
  // this prevents other modals (like chat) from triggering navigation when they use Enter


  return (
    <Frame
      style={{
        display: "flex",
        flexDirection: "row",
        gap: "4px",
        marginBottom: "4px",
        margin: "2px",
        alignItems: "center",
      }}
    >
      <DoubleVertDivider />
      <span style={{ whiteSpace: "nowrap", paddingLeft: "4px", paddingRight: "0px" }}>
        Address
      </span>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingLeft: "4px",
          paddingRight: "4px",
          border: "2px inset",
          borderColor: "#a0a0a0",
          backgroundColor: "#ffffff",
          height: "24px",
        }}
      >
        <IExploreFile style={{ marginRight: "4px", flexShrink: 0 }} />
        <input
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            backgroundColor: "transparent",
            fontFamily: "inherit",
            fontSize: "12px",
          }}
          value={urlValue}
          onChange={(e) => setUrlValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // keep enter behavior local to the address input
              e.preventDefault();
              e.stopPropagation();
              navigateToURL(urlValue);
            }
          }}
        />
      </div>
      <CustomButton
        onClick={() => navigateToURL(urlValue)}
        onMouseEnter={() => setGoButtonHover(true)}
        onMouseLeave={() => setGoButtonHover(false)}
        style={{
          height: "24px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        {goButtonHover ? <IExploreGoActive /> : <IExploreGoInactive />}
        Go
      </CustomButton>
    </Frame>
  );
}

function ProgressFrame() {
  const { isLoading, loadingPercent } = useWebsite();

  const width = "150px";

  const wrapperStyle = {
    // height: contract.space[20],
    position: "relative",
    textAlign: "center",
    // borderLeft: contract.space[1],
    // borderLeftStyle: 'solid',
    // borderLeftColor: contract.colors.borderDark,
    // borderTop: contract.space[1],
    // borderTopStyle: 'solid',
    // borderTopColor: contract.colors.borderDark,
  };

  const progressStyle = {
    height: contract.space[17],
    lineHeight: contract.space[18],
    marginLeft: contract.space[2],
    marginTop: contract.space[2],
    backgroundColor: contract.colors.progress,
    color: contract.colors.materialTextInvert,
  };

  const backStyle = {
    height: contract.space[17],
    lineHeight: contract.space[20],
    marginLeft: contract.space[2],
    marginTop: contract.space[2],
    // boxShadow: contract.shadows.input,
    backgroundColor: contract.colors.inputBackground,
    color: contract.colors.materialText,
  };

  const containerStyle = {
    position: "absolute",
    top: "0",
    left: "0",
    overflow: "hidden",
  };

  const textContent = useMemo(() => (`Loading... ${loadingPercent}%`), [loadingPercent]);

  if(!isLoading || loadingPercent >= 100) {
    return (
      <Frame
        boxShadow="$in"
        // h="20px"
        style={{
          display: "flex",
          marginLeft: "1px",
          marginRight: "1px",
          paddingLeft: "4px",
          paddingRight: "4px",
          paddingTop: "4px",
          fontSize: "12px",

          width: width,
          maxWidth: width,
        }}
      >
        Done
      </Frame>
    );
  }

  return (
    <Frame
      boxShadow="$in"
      // h="20px"
      style={{
        display: "flex",
        // marginLeft: "1px",
        // marginRight: "1px",
        paddingLeft: "2px",
        paddingRight: "2px",
        // paddingTop: "4px",
        fontSize: "12px",

        width: width,
        maxWidth: width,

        ...wrapperStyle,
      }}
    >
      <Frame style={backStyle} width={width}>{textContent}</Frame>
      <Frame style={containerStyle} width={`${loadingPercent}%`}>
        <Frame style={progressStyle} width={width}>{textContent}</Frame>
      </Frame>
    </Frame>
  );
}

function BottomStatusBar() {
  const { isLoading, loadingPercent } = useWebsite();

  const statusStyle: CSSProperties = {
    display: "flex",
    marginLeft: "1px",
    marginRight: "1px",
    paddingLeft: "4px",
    paddingRight: "4px",
    paddingTop: "4px",
    fontSize: "12px",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        marginTop: "2px",
      }}
    >
      <ProgressFrame />
      <Frame
        boxShadow="$in"
        h="20px"
        style={{
          ...statusStyle,
          flexGrow: 1,
          fontSize: "12px",
        }}
      ></Frame>
      <Frame
        boxShadow="$in"
        h="20px"
        style={{
          ...statusStyle,
          fontSize: "12px",
        }}
      ></Frame>
      <Frame
        boxShadow="$in"
        h="20px"
        style={{
          ...statusStyle,
          fontSize: "12px",
        }}
      ></Frame>
      <Frame
        boxShadow="$in"
        h="20px"
        style={{
          ...statusStyle,
          fontSize: "12px",
        }}
      ></Frame>
      <Frame
        boxShadow="$in"
        h="20px"
        style={{
          ...statusStyle,
          flexGrow: 1,
          fontSize: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Ie variant="16x16_8" />
          The Dom Zone
        </div>
      </Frame>
      <Frame
        boxShadow="$in"
        h="20px"
        style={{
          ...statusStyle,
          fontSize: "12px",
        }}
      ></Frame>
      <Frame
        boxShadow="$in"
        h="20px"
        style={{
          ...statusStyle,
          fontSize: "12px",
        }}
      >
        <Lock variant="16x16_4" />
      </Frame>
      <Frame
        boxShadow="$in"
        h="20px"
        w="16px"
        style={{
          ...statusStyle,
          fontSize: "12px",
        }}
      ></Frame>
    </div>
  );
}

export const InternetClientDef = {
  id: WINDOW_NAMES.InternetClient,
  title: "Internal Exploder 4.0",
  icon: <Ie variant="16x16_8" />,
  hasButton: true,
};

function InternetClient({
  instanceId,
  defaultClosed = false,
}: {
  instanceId: string;
  defaultClosed?: boolean;
}) {
  const { closeModal, getModalData } = useApp();
  const { pageTitle, pageIcon, navigateToURL, isLoading } = useWebsite();
  const navigationTrackerRef = useRef<{ url: string; id: string } | null>(null);

  // load initial URL from modal data if available
  useEffect(() => {
    const data = getModalData(instanceId);
    console.log("[InternetClient]", instanceId, "effect running, retrieved modal data:", data);
    if (data?.initialURL) {
      // only navigate if this is a new URL or different modal instance
      if (
        !navigationTrackerRef.current ||
        navigationTrackerRef.current.url !== data.initialURL ||
        navigationTrackerRef.current.id !== data.id
      ) {
        console.log("[InternetClient]", instanceId, "navigating to:", data.initialURL);
        navigationTrackerRef.current = { url: data.initialURL, id: data.id };
        navigateToURL(data.initialURL);
      }
    }
  }, [instanceId, getModalData, navigateToURL]);

  const icon = useMemo<React.ReactElement>(() => {
    if (React.isValidElement(pageIcon)) {
      return pageIcon;
    }

    return <Mshtml32529 variant="32x32_4" />;
  }, [pageIcon]);

  const sizeProps = useMemo(
    () => getModalSizeProps("550px", "650px", "normal", "240px", "450px"),
    [],
  );

  const handleCloseModal = () => {
    closeModal(instanceId);
  };

  return (
    <Modal
      key={instanceId}
      id={instanceId}
      icon={icon}
      title={
        pageTitle && pageTitle != ""
          ? `${pageTitle} - ${InternetClientDef.title}`
          : InternetClientDef.title
      }
      dragOptions={sizeProps.dragOptions}
      titleBarOptions={[
        <Modal.Minimize key="minimize" />,
        <TitleBar.Close key="close" onClick={handleCloseModal} />,
      ]}
      defaultClosed={defaultClosed}
      height={sizeProps.height}
      width={sizeProps.width}
      isResizeable={true}
      minWidth={sizeProps.minWidth}
      minHeight={sizeProps.minHeight}
      menu={[
        <DoubleVertDivider
          key="menu-divider"
          style={{
            marginLeft: "2px",
            marginRight: "2px",
          }}
        />,
        {
          name: "File",
          list: (
            <List width="120px">
              <List.Item onClick={handleCloseModal}>Exit</List.Item>
            </List>
          ),
        },
        {
          name: "Edit",
          list: (
            <List width="120px">
              <List.Item onClick={handleCloseModal}>Exit</List.Item>
            </List>
          ),
        },
        {
          name: "View",
          list: (
            <List width="120px">
              <List.Item onClick={handleCloseModal}>Exit</List.Item>
            </List>
          ),
        },
        {
          name: "Go",
          list: (
            <List width="120px">
              <List.Item onClick={handleCloseModal}>Exit</List.Item>
            </List>
          ),
        },
        {
          name: "Favourites",
          list: (
            <List width="120px">
              <List.Item onClick={handleCloseModal}>Exit</List.Item>
            </List>
          ),
        },
        {
          name: "Help",
          list: (
            <List width="120px">
              <List.Item onClick={handleCloseModal}>Exit</List.Item>
            </List>
          ),
        },
      ]}
    >
      <Modal.Content style={{ padding: 0, overflow: isLoading ? 'hidden' : 'auto' }}>
        <TopButtonToolbar />

        <HorizDivider />

        <AddressBar />

        <Frame
          boxShadow="$in"
          style={{
            display: "flex",
            flex: 1,
            flexGrow: 1,
            overflow: isLoading ? "hidden" : "auto",
            padding: "1px",
            backgroundColor: isLoading ? "#ffffff" : undefined,
            alignItems: "stretch",
            position: "relative",
          }}
          className="website-content"
        >
          {isLoading ? (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "#ffffff",
                overflow: "hidden",
                touchAction: "none",
              }}
              onWheel={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onWheelCapture={(e) => e.preventDefault()}
              onTouchMove={(e) => e.preventDefault()}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerMove={(e) => e.stopPropagation()}
              aria-hidden="true"
            />
          ) : (
            <WebsiteController />
          )}
        </Frame>

        <BottomStatusBar />
      </Modal.Content>
    </Modal>
  );
}

export default function InternetClientWithProvider({
  instanceId,
  initialURL,
  defaultClosed,
}: {
  instanceId: string;
  initialURL?: string;
  defaultClosed?: boolean;
}) {
  return (
    <WebsiteProvider instanceId={instanceId} initialURL={initialURL}>
      <InternetClient instanceId={instanceId} defaultClosed={defaultClosed} />
    </WebsiteProvider>
  );
}
